"use client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { MessageCircle, Pencil, Trash2, EyeOff, Eye, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";
import { fetchWrapper } from "@/helpers";
import { userService } from "@/services/user.service";
import { usePWA } from "@/utlis/usePWA";
import { useIsMobile } from "@/utlis/useIsMobile";
import Avatar from "@/components/common/Avatar";
import QuestionForm from "./QuestionForm";
import { ConfirmDrawer, EditTextDrawer } from "../reviews/MobileDialogs";

function formatDate(dateStr) {
	return new Date(dateStr).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" });
}

function AnswerItem({ answer, questionID, isAdmin, onEditAnswer, onDeleteAnswer, onHideAnswer }) {
	const canManage = answer.isMine || isAdmin;

	if (answer.hidden && !isAdmin) return null;

	return (
		<div className={`tf-review-comment ${answer.hidden ? "is-hidden" : ""}`}>
			<div className='tf-review-comment-head'>
				{answer.isAdminReply ? (
					<img src='/images/logo/emagyl.png' alt='' className='tf-review-admin-logo' />
				) : (
					<Avatar url={answer.userAvatar} name={answer.userName} size={26} />
				)}
				<span className='tf-review-comment-author'>{answer.userName}</span>
				{answer.isAdminReply && <span className='tf-review-admin-badge'>Răspuns oficial</span>}
				<span className='tf-review-comment-date'>{formatDate(answer.createdAt)}</span>
				{isAdmin && answer.hidden && <span className='tf-review-flag-badge'>ascuns</span>}

				<div className='tf-review-comment-actions-admin'>
					{answer.isMine && (
						<button type='button' onClick={() => onEditAnswer({ ...answer, questionID })} aria-label='Editează răspunsul'>
							<Pencil size={13} />
						</button>
					)}
					{isAdmin && (
						<button type='button' onClick={() => onHideAnswer({ ...answer, questionID })} aria-label={answer.hidden ? "Arată răspunsul" : "Ascunde răspunsul"}>
							{answer.hidden ? <Eye size={13} /> : <EyeOff size={13} />}
						</button>
					)}
					{canManage && (
						<button type='button' onClick={() => onDeleteAnswer({ ...answer, questionID })} aria-label='Șterge răspunsul'>
							<Trash2 size={13} />
						</button>
					)}
				</div>
			</div>

			<p className='tf-review-comment-text'>{answer.text}</p>
		</div>
	);
}

export default function QuestionsSection({ product }) {
	const router = useRouter();
	const isPWA = usePWA();
	const isMobile = useIsMobile();
	const isCompact = isPWA || isMobile;

	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [showForm, setShowForm] = useState(false);
	const [formMode, setFormMode] = useState("create");
	const [editingQuestion, setEditingQuestion] = useState(null);
	const [openAnswers, setOpenAnswers] = useState({});
	const [newAnswer, setNewAnswer] = useState({});
	const [newAnswerAsAdmin, setNewAnswerAsAdmin] = useState({});
	const [confirmDialog, setConfirmDialog] = useState(null);
	const [editDialog, setEditDialog] = useState(null);
	const dialogResolverRef = useRef(null);

	const fetchQuestions = useCallback(async () => {
		setLoading(true);
		try {
			const params = new URLSearchParams({ page });
			const res = await fetchWrapper.get(`/api/questions/${product.id}?${params.toString()}`);
			setData(res);
		} catch (err) {
			console.error("Eroare la încărcarea întrebărilor:", err);
		} finally {
			setLoading(false);
		}
	}, [product.id, page]);

	useEffect(() => {
		fetchQuestions();
	}, [fetchQuestions]);

	const askConfirm = (title, text, confirmText = "Da, șterge") => {
		if (!isCompact) {
			return Swal.fire({
				title,
				text,
				icon: "warning",
				showCancelButton: true,
				confirmButtonText: confirmText,
				cancelButtonText: "Anulează",
				confirmButtonColor: "#e53e3e",
				cancelButtonColor: "#718096",
				reverseButtons: true,
				focusCancel: true,
			}).then((r) => r.isConfirmed);
		}
		return new Promise((resolve) => {
			dialogResolverRef.current = resolve;
			setConfirmDialog({ title, text, confirmText });
		});
	};

	const askEditText = (title, initialValue) => {
		if (!isCompact) {
			return Swal.fire({
				title,
				input: "textarea",
				inputValue: initialValue,
				showCancelButton: true,
				confirmButtonText: "Salvează",
				cancelButtonText: "Anulează",
			}).then((r) => (r.isConfirmed ? r.value : null));
		}
		return new Promise((resolve) => {
			dialogResolverRef.current = resolve;
			setEditDialog({ title, initialValue });
		});
	};

	const ensureLoggedIn = () => {
		if (userService.userValue) return true;
		toast.info("Trebuie să fii conectat pentru a continua.");
		router.push("/autentificare");
		return false;
	};

	const handleAskQuestionClick = () => {
		if (!ensureLoggedIn()) return;
		setFormMode("create");
		setEditingQuestion(null);
		setShowForm(true);
	};

	const handleSubmitAnswer = async (questionID, text, asAdmin) => {
		if (!ensureLoggedIn()) return;
		try {
			await fetchWrapper.post(`/api/questions/${questionID}/answer`, { text, asAdmin });
			fetchQuestions();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Răspunsul nu a putut fi trimis.");
		}
	};

	const handleEditAnswer = async (answer) => {
		const newText = await askEditText("Editează răspunsul", answer.text);
		if (!newText || !newText.trim()) return;
		try {
			await fetchWrapper.put(`/api/questions/${answer.questionID}/answer/${answer.id}`, { text: newText.trim() });
			fetchQuestions();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Răspunsul nu a putut fi actualizat.");
		}
	};

	const handleDeleteAnswer = async (answer) => {
		const confirmed = await askConfirm("Ștergi acest răspuns?", "Această acțiune nu poate fi anulată.");
		if (!confirmed) return;
		try {
			if (answer.isMine && !data.isAdmin) {
				await fetchWrapper.delete(`/api/questions/${answer.questionID}/answer/${answer.id}`);
			} else {
				await fetchWrapper.delete(`/api/admin/questions/answer/${answer.id}`);
			}
			fetchQuestions();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Răspunsul nu a putut fi șters.");
		}
	};

	const handleHideAnswer = async (answer) => {
		try {
			await fetchWrapper.put(`/api/admin/questions/answer/${answer.id}`, { hidden: !answer.hidden });
			fetchQuestions();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Acțiunea a eșuat.");
		}
	};

	const handleEditQuestion = (question) => {
		setFormMode(data.isAdmin && !question.isMine ? "adminEdit" : "ownEdit");
		setEditingQuestion(question);
		setShowForm(true);
	};

	const handleDeleteQuestion = async (question) => {
		const confirmed = await askConfirm("Ștergi această întrebare?", "Răspunsurile asociate vor fi șterse automat.");
		if (!confirmed) return;
		try {
			if (question.isMine && !data.isAdmin) {
				await fetchWrapper.delete(`/api/questions/${question.id}/own`);
			} else {
				await fetchWrapper.delete(`/api/admin/questions/${question.id}`);
			}
			fetchQuestions();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Întrebarea nu a putut fi ștearsă.");
		}
	};

	const handleHideQuestion = async (question) => {
		try {
			await fetchWrapper.put(`/api/admin/questions/${question.id}`, { hidden: !question.hidden });
			fetchQuestions();
		} catch (err) {
			toast.error(typeof err === "string" ? err : "Acțiunea a eșuat.");
		}
	};

	if (loading && !data) return <div className='tf-reviews-loading'>Se încarcă întrebările...</div>;
	if (!data) return null;

	const { questions, totalPages, isAdmin } = data;

	return (
		<div className='tf-reviews-section'>
			<div className='tf-reviews-summary-cta'>
				<div className='tf-reviews-summary-cta-title'>Ai o întrebare despre acest produs?</div>
				<p>Pune o întrebare și un alt client sau echipa noastră îți poate răspunde.</p>
				<button type='button' className='tf-reviews-add-btn' onClick={handleAskQuestionClick}>
					Pune o întrebare
				</button>
			</div>

			<div className='tf-reviews-list'>
				{questions.length === 0 && <div className='tf-reviews-empty'>Momentan nu există întrebări pentru acest produs.</div>}

				{questions.map((question) => {
					const canManageQuestion = isAdmin || question.isMine;
					return (
						<div className={`tf-review-card ${question.hidden ? "is-hidden" : ""}`} key={question.id}>
							{canManageQuestion && (
								<div className='tf-review-card-admin-actions'>
									{isAdmin && question.hidden && <span className='tf-review-flag-badge'>ascuns</span>}
									<button type='button' onClick={() => handleEditQuestion(question)} aria-label='Editează'>
										<Pencil size={16} />
									</button>
									{isAdmin && (
										<button type='button' onClick={() => handleHideQuestion(question)} aria-label={question.hidden ? "Arată" : "Ascunde"}>
											{question.hidden ? <Eye size={16} /> : <EyeOff size={16} />}
										</button>
									)}
									<button type='button' onClick={() => handleDeleteQuestion(question)} aria-label='Șterge'>
										<Trash2 size={16} />
									</button>
								</div>
							)}

							<div className='tf-review-card-head'>
								<Avatar url={question.userAvatar} name={question.userName} size={36} />
							</div>

							<p className='tf-review-card-text'>{question.text}</p>

							<div className='tf-review-card-meta'>
								<span className='tf-review-card-author'>{question.userName}</span>
								<span className='tf-review-card-date'>{formatDate(question.createdAt)}</span>
							</div>

							<div className='tf-review-card-actions'>
								<button
									type='button'
									className='tf-review-comment-btn'
									onClick={() => setOpenAnswers((prev) => ({ ...prev, [question.id]: !prev[question.id] }))}>
									<MessageCircle size={16} /> {question.answersCount} răspunsuri
								</button>
							</div>

							{openAnswers[question.id] && (
								<div className='tf-review-comments-block'>
									{question.answers.map((answer) => (
										<AnswerItem
											key={answer.id}
											answer={{ ...answer, questionID: question.id }}
											questionID={question.id}
											isAdmin={isAdmin}
											onEditAnswer={handleEditAnswer}
											onDeleteAnswer={handleDeleteAnswer}
											onHideAnswer={handleHideAnswer}
										/>
									))}

									<div className='tf-review-new-comment'>
										<textarea
											rows={2}
											placeholder='Scrie un răspuns...'
											value={newAnswer[question.id] || ""}
											onChange={(e) => setNewAnswer((prev) => ({ ...prev, [question.id]: e.target.value }))}
										/>
										{isAdmin && (
											<label className='tf-review-admin-checkbox'>
												<input
													type='checkbox'
													checked={!!newAnswerAsAdmin[question.id]}
													onChange={(e) => setNewAnswerAsAdmin((prev) => ({ ...prev, [question.id]: e.target.checked }))}
												/>
												<ShieldCheck size={13} /> Postează răspuns ca administrator
											</label>
										)}
										<button
											type='button'
											onClick={() => {
												const value = (newAnswer[question.id] || "").trim();
												if (!value) return;
												handleSubmitAnswer(question.id, value, !!newAnswerAsAdmin[question.id]);
												setNewAnswer((prev) => ({ ...prev, [question.id]: "" }));
												setNewAnswerAsAdmin((prev) => ({ ...prev, [question.id]: false }));
											}}>
											Trimite
										</button>
									</div>
								</div>
							)}
						</div>
					);
				})}
			</div>

			{totalPages > 1 && (
				<div className='tf-reviews-pagination'>
					<button type='button' disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
						<ChevronLeft size={18} />
					</button>
					<span>
						Pagina {page} din {totalPages}
					</span>
					<button type='button' disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
						<ChevronRight size={18} />
					</button>
				</div>
			)}

			{showForm && (
				<QuestionForm
					mode={formMode}
					productID={product.id}
					initialData={editingQuestion}
					onClose={() => setShowForm(false)}
					onSuccess={fetchQuestions}
				/>
			)}

			{confirmDialog && (
				<ConfirmDrawer
					title={confirmDialog.title}
					text={confirmDialog.text}
					confirmText={confirmDialog.confirmText}
					onConfirm={() => {
						dialogResolverRef.current?.(true);
						setConfirmDialog(null);
					}}
					onCancel={() => {
						dialogResolverRef.current?.(false);
						setConfirmDialog(null);
					}}
				/>
			)}

			{editDialog && (
				<EditTextDrawer
					title={editDialog.title}
					initialValue={editDialog.initialValue}
					onSave={(value) => {
						dialogResolverRef.current?.(value);
						setEditDialog(null);
					}}
					onCancel={() => {
						dialogResolverRef.current?.(null);
						setEditDialog(null);
					}}
				/>
			)}
		</div>
	);
}
